/**
 * ASCII Art Generator - 特效辅助函数
 */

import type { EffectDirection } from '../types'

// ============================================
// 方向向量计算
// ============================================

/** 获取方向向量 */
export function getDirectionVector(direction: EffectDirection): { dx: number; dy: number } {
  switch (direction) {
    case 'up': return { dx: 0, dy: -1 }
    case 'down': return { dx: 0, dy: 1 }
    case 'left': return { dx: -1, dy: 0 }
    case 'right': return { dx: 1, dy: 0 }
    case 'top-left': return { dx: -0.707, dy: -0.707 }
    case 'top-right': return { dx: 0.707, dy: -0.707 }
    case 'bottom-left': return { dx: -0.707, dy: 0.707 }
    case 'bottom-right': return { dx: 0.707, dy: 0.707 }
    default: return { dx: 1, dy: 0 }
  }
}

/** 方向是否为水平方向 */
export function isHorizontalDirection(direction: EffectDirection): boolean {
  return direction === 'left' || direction === 'right' ||
         direction === 'top-left' || direction === 'top-right' ||
         direction === 'bottom-left' || direction === 'bottom-right'
}

/** 方向是否为垂直方向 */
export function isVerticalDirection(direction: EffectDirection): boolean {
  return direction === 'up' || direction === 'down'
}

// ============================================
// 颜色处理
// ============================================

/** RGB 到 HSL 转换 */
export function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 }
}

/** HSL 到 RGB 转换 */
export function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h /= 360
  s /= 100
  l /= 100

  let r, g, b

  if (s === 0) {
    r = g = b = l
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1/6) return p + (q - p) * 6 * t
      if (t < 1/2) return q
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
      return p
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q

    r = hue2rgb(p, q, h + 1/3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1/3)
  }

  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) }
}

/** 应用色相偏移 */
export function applyHueShift(
  r: number,
  g: number,
  b: number,
  shift: number
): { r: number; g: number; b: number } {
  const hsl = rgbToHsl(r, g, b)
  hsl.h = (hsl.h + shift) % 360
  return hslToRgb(hsl.h, hsl.s, hsl.l)
}

// ============================================
// 像素处理
// ============================================

/** 安全获取像素索引 */
export function getPixelIndex(x: number, y: number, width: number, height: number): number | null {
  if (x < 0 || x >= width || y < 0 || y >= height) {
    return null
  }
  return (y * width + x) * 4
}

/** 限制值在范围内 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

// ============================================
// 随机生成
// ============================================

/** 生成随机整数 */
export function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

/** 生成随机浮点数 */
export function randomFloat(min: number, max: number): number {
  return Math.random() * (max - min) + min
}

// ============================================
// Matrix 字符集
// ============================================

/** Matrix 日文字符集（片假名 + 数字） */
export const MATRIX_CHARS = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789'

/** 获取随机 Matrix 字符 */
export function getRandomMatrixChar(): string {
  return MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)]
}

/** ASCII 备选字符集 */
export const MATRIX_ASCII_CHARS = '@#$%^&*(){}[]|\\/<>~`0123456789'

/** 获取随机 ASCII Matrix 字符 */
export function getRandomAsciiMatrixChar(): string {
  return MATRIX_ASCII_CHARS[Math.floor(Math.random() * MATRIX_ASCII_CHARS.length)]
}